import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ContactListItem, CreateOrganizationInput, OrganizationListItem, Paginated, UpdateOrganizationInput } from '@crm/shared';
import { api } from '../../api/client';
import type { ListParams } from '../../components/list-params';

export interface OrganizationDetail extends OrganizationListItem {
  dealCount: number;
  counts: { contacts: number; deals: number; openTasks: number };
}

export function useOrganizations(params: ListParams) {
  return useQuery({
    queryKey: ['organizations', params],
    queryFn: async () => (await api.get<Paginated<OrganizationListItem>>('/organizations', { params })).data,
    placeholderData: keepPreviousData,
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: ['organization', id],
    queryFn: async () => (await api.get<OrganizationDetail>(`/organizations/${id}`)).data,
    enabled: !!id,
  });
}

export function useOrganizationContacts(id: string | undefined, params: ListParams) {
  return useQuery({
    queryKey: ['organization-contacts', id, params],
    queryFn: async () => (await api.get<Paginated<ContactListItem>>(`/organizations/${id}/contacts`, { params })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}

export async function searchOrganizations(q: string) {
  return (await api.get<Paginated<OrganizationListItem>>('/organizations', { params: { q, pageSize: 10, sortBy: 'name', sortDir: 'asc' } })).data.items;
}

function useInvalidateOrgs() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: ['organizations'] });
    void qc.invalidateQueries({ queryKey: ['dashboard'] });
    if (id) {
      void qc.invalidateQueries({ queryKey: ['organization', id] });
      void qc.invalidateQueries({ queryKey: ['activities'] });
    }
  };
}

export function useCreateOrganization() {
  const inv = useInvalidateOrgs();
  return useMutation({
    mutationFn: async (input: CreateOrganizationInput) => (await api.post<OrganizationListItem>('/organizations', input)).data,
    onSuccess: () => inv(),
  });
}

export function useUpdateOrganization() {
  const inv = useInvalidateOrgs();
  return useMutation({
    mutationFn: async ({ id, ...input }: UpdateOrganizationInput & { id: string }) =>
      (await api.patch<OrganizationListItem>(`/organizations/${id}`, input)).data,
    onSuccess: (_d, v) => inv(v.id),
  });
}

export function useDeleteOrganizations() {
  const inv = useInvalidateOrgs();
  return useMutation({
    mutationFn: async (ids: string[]) => (await api.post<{ deleted: number }>('/organizations/bulk-delete', { ids })).data,
    onSuccess: () => inv(),
  });
}
